import React, { PureComponent } from 'react';
import './ResultsChart.css';
import {
  LineChart,
  Line,
  ResponsiveContainer,
  XAxis,
  Tooltip,
  YAxis,
  Legend,
  CartesianGrid,
  ReferenceDot,
} from 'recharts';
import Card from 'react-bootstrap/Card';
import Spinner from 'react-bootstrap/Spinner';
import moment from 'moment';
import { NumericFormat } from 'react-number-format';
import { formatScheduleCadenceForViewer } from '../utils/scheduleCadence';

/**
 * Minimal chart:
 * - Hide Y-axis labels
 * - Show no horizontal grid lines
 * - Plot only account value and the benchmark
 * - Keep lifecycle and cash-flow events as interactive point markers on the account line
 */

export const getPortfolioValueDomain = (rows) => {
  const values = (Array.isArray(rows) ? rows : [])
    .flatMap((row) => [row?.portfolio_value, row?.spy_benchmark])
    .filter((value) => value !== null && value !== undefined && value !== '')
    .map((value) => Number(value))
    .filter((value) => Number.isFinite(value));

  if (values.length === 0) {
    return [0, 100];
  }

  let minVal = values.reduce((min, value) => Math.min(min, value), Infinity);
  let maxVal = values.reduce((max, value) => Math.max(max, value), -Infinity);

  const range = maxVal - minVal;
  const padding = range > 0
    ? range * 0.08
    : Math.max(Math.abs(maxVal) * 0.02, 1);

  minVal -= padding;
  maxVal += padding;

  return [minVal, maxVal];
};

const markerTimestamp = (marker) => marker?.timestamp || marker?.occurredAt || marker?.occurred_at || null;

export const filterMarkersForChart = (markers, rows, frequency = 'day') => {
  const dates = (Array.isArray(rows) ? rows : [])
    .map((row) => Number(row?.date))
    .filter(Number.isFinite);
  if (dates.length === 0) return [];
  const first = Math.min(...dates);
  const last = Math.max(...dates);
  const bucketMs = frequency === 'minute'
    ? 60 * 1000
    : frequency === 'hour'
      ? 60 * 60 * 1000
      : 24 * 60 * 60 * 1000;
  return (Array.isArray(markers) ? markers : [])
    .flatMap((marker) => {
      const timestamp = new Date(markerTimestamp(marker)).getTime();
      return Number.isFinite(timestamp) && timestamp >= first && timestamp < last + bucketMs
        ? [{ ...marker, chartTimestamp: timestamp }]
        : [];
    })
    .sort((left, right) => (
      left.chartTimestamp - right.chartTimestamp
      || String(left.id || '').localeCompare(String(right.id || ''))
    ));
};

const markerType = (marker) => marker?.marker_type || marker?.markerType || 'event';

const markerTitle = (marker) => {
  const type = markerType(marker);
  if (type === 'deposit') return 'Deposit';
  if (type === 'withdrawal') return 'Withdrawal';
  if (type === 'deployment_start') return 'Deployment started';
  if (type === 'deployment_stop') return 'Deployment stopped';
  return marker?.label || 'Account event';
};

const cadencePolicyFromLegacyLabel = (cadence) => {
  const match = String(cadence || '').trim().match(/^Daily at (\d{1,2}:\d{2})\s+(.+)$/i);
  if (!match) return null;
  return {
    cadence: {
      type: 'specific_times',
      times: [match[1]],
      timezone: match[2],
      daysOfWeek: [0, 1, 2, 3, 4, 5, 6],
    },
  };
};

const displayCadence = (marker) => {
  const policy = marker?.cadence_policy
    || marker?.cadencePolicy
    || cadencePolicyFromLegacyLabel(marker?.cadence);
  return formatScheduleCadenceForViewer(policy, {
    referenceTimestamp: markerTimestamp(marker),
  }) || marker?.cadence || null;
};

export const markerDetails = (marker) => {
  const type = markerType(marker);
  if (type === 'deposit' || type === 'withdrawal') {
    const amount = Number(marker?.amount);
    const formatted = Number.isFinite(amount)
      ? new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: marker?.currency || 'USD',
      }).format(Math.abs(amount))
      : '';
    return [formatted, marker?.description].filter(Boolean).join(' · ');
  }
  const revision = marker?.revision_version ?? marker?.revisionVersion;
  const mode = String(marker?.deployment_mode || marker?.deploymentMode || '').replace(/_/g, ' ');
  const modeLabel = mode ? mode.replace(/\b\w/g, (character) => character.toUpperCase()) : null;
  const cadence = displayCadence(marker);
  const cadenceRepeatsMode = cadence && modeLabel
    && cadence.toLowerCase().replace(/\s+/g, ' ').trim() === modeLabel.toLowerCase().replace(/\s+/g, ' ').trim();
  return [
    revision !== null && revision !== undefined ? `Revision ${revision}` : null,
    modeLabel,
    cadenceRepeatsMode ? null : cadence,
  ].filter(Boolean).join(' · ');
};

const markerColor = (marker) => {
  const type = markerType(marker);
  if (type === 'deposit') return '#4caf50';
  if (type === 'withdrawal') return '#ff9800';
  if (type === 'deployment_stop') return '#b39ddb';
  return '#64b5f6';
};

const projectValueAtTimestamp = (rows, timestamp) => {
  const points = (Array.isArray(rows) ? rows : [])
    .flatMap((row) => {
      if (
        row?.date === null || row?.date === undefined || row?.date === ''
        || row?.portfolio_value === null || row?.portfolio_value === undefined || row?.portfolio_value === ''
      ) return [];
      const date = Number(row?.date);
      const value = Number(row?.portfolio_value);
      return Number.isFinite(date) && Number.isFinite(value) ? [{ date, value }] : [];
    })
    .sort((left, right) => left.date - right.date);

  if (points.length === 0 || !Number.isFinite(timestamp)) return null;
  if (timestamp <= points[0].date) return points[0].value;
  if (timestamp >= points[points.length - 1].date) return points[points.length - 1].value;

  for (let index = 1; index < points.length; index += 1) {
    const right = points[index];
    if (right.date < timestamp) continue;
    const left = points[index - 1];
    if (right.date === left.date) return right.value;
    const progress = (timestamp - left.date) / (right.date - left.date);
    return left.value + ((right.value - left.value) * progress);
  }
  return points[points.length - 1].value;
};

export const projectMarkersOnAccountSeries = (markers, rows, frequency = 'day') => (
  filterMarkersForChart(markers, rows, frequency).flatMap((marker) => {
    const chartValue = projectValueAtTimestamp(rows, marker.chartTimestamp);
    return Number.isFinite(chartValue) ? [{ ...marker, chartValue }] : [];
  }).reduce((projected, marker) => {
    const bucketMs = frequency === 'minute'
      ? 60 * 1000
      : frequency === 'hour'
        ? 60 * 60 * 1000
        : 24 * 60 * 60 * 1000;
    const markerBucket = Math.floor(marker.chartTimestamp / bucketMs);
    const stackIndex = projected.filter((candidate) => candidate.markerBucket === markerBucket).length;
    // Keep nearby touch targets from covering each other on mobile. The
    // markers stay visually compact, while each one remains independently
    // tappable when several lifecycle events land in the same chart bucket.
    const offsets = [0, -38, 38, -76, 76];
    return [...projected, {
      ...marker,
      markerBucket,
      visualOffsetY: offsets[stackIndex] ?? (-38 * stackIndex),
    }];
  }, [])
);

const MarkerGlyph = ({ type }) => {
  if (type === 'deployment_start') {
    return <path d="M -2.5 -3.5 L 4 0 L -2.5 3.5 Z" fill="#08111d" pointerEvents="none" />;
  }
  if (type === 'deployment_stop') {
    return <rect x="-2.75" y="-2.75" width="5.5" height="5.5" rx="0.75" fill="#08111d" pointerEvents="none" />;
  }
  if (type === 'deposit') {
    return (
      <g stroke="#08111d" strokeWidth="1.8" strokeLinecap="round" pointerEvents="none">
        <line x1="-3" y1="0" x2="3" y2="0" />
        <line x1="0" y1="-3" x2="0" y2="3" />
      </g>
    );
  }
  return <line x1="-3" y1="0" x2="3" y2="0" stroke="#08111d" strokeWidth="1.8" strokeLinecap="round" pointerEvents="none" />;
};

const ChartEventMarker = ({ cx, cy, marker, isActive, onActivate, onBlur, onHoverEnd }) => {
  const type = markerType(marker);
  const title = markerTitle(marker);
  const details = markerDetails(marker);
  const timestampLabel = moment(marker.chartTimestamp).local().format('MMM D, YYYY h:mm a');
  const ariaLabel = [title, details, timestampLabel].filter(Boolean).join('. ');
  const usesCoarsePointer = typeof window !== 'undefined'
    && typeof window.matchMedia === 'function'
    && window.matchMedia('(pointer: coarse)').matches;

  return (
    <g
      aria-expanded={isActive}
      aria-label={ariaLabel}
      className={`chart-event-marker chart-event-marker--${type}`}
      data-marker-id={marker.id}
      onBlur={onBlur}
      onClick={onActivate}
      onFocus={onActivate}
      onKeyDown={(event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          onActivate(event);
        }
      }}
      onMouseEnter={onActivate}
      onMouseLeave={onHoverEnd}
      role="button"
      tabIndex={0}
      transform={`translate(${cx}, ${cy + (marker.visualOffsetY || 0)})`}
    >
      <circle className="chart-event-marker__hitbox" r={usesCoarsePointer ? 18 : 12} />
      <circle
        className="chart-event-marker__dot"
        fill={markerColor(marker)}
        r={isActive ? 11 : 10}
      />
      <MarkerGlyph type={type} />
    </g>
  );
};

class CustomizedAxisTick extends PureComponent {
  render() {
    const { x, y, payload, frequency } = this.props;
    const date = new Date(payload.value);

    let formattedDate;
    switch (frequency) {
      case 'minute':
        formattedDate = moment(date).format('MMM D, h:mm a');
        break;
      case 'hour':
        formattedDate = moment(date).format('MMM D, h A');
        break;
      case 'day':
      default:
        formattedDate = moment(date).format('MMM D');
        break;
    }

    return (
      <g transform={`translate(${x},${y})`}>
        <text
          x={0}
          y={0}
          dy={16}
          textAnchor="end"
          fill="#cccccc"
          fontSize={10}
          transform="rotate(-10)"
        >
          {formattedDate}
        </text>
      </g>
    );
  }
}

export default function ResultsChart({
  data,
  frequency,
  isLoading,
  trendDirection,
  minimal = false,
  chartHeight,
  emptyTitle = 'No data for this timeframe.',
  emptyDescription = '',
  markers = [],
  cashFlowAvailability,
}) {
  const chartContainerRef = React.useRef(null);
  const [clickedMarkerId, setClickedMarkerId] = React.useState(null);
  const [focusedMarkerId, setFocusedMarkerId] = React.useState(null);
  const [hoveredMarkerId, setHoveredMarkerId] = React.useState(null);
  const [markerDetailPosition, setMarkerDetailPosition] = React.useState(null);
  React.useEffect(() => {
    const dismissMarkerDetail = (event) => {
      const target = event.target;
      if (target instanceof Element && target.closest('.chart-event-marker')) return;
      setClickedMarkerId(null);
      setFocusedMarkerId(null);
      setHoveredMarkerId(null);
      setMarkerDetailPosition(null);
    };

    document.addEventListener('pointerdown', dismissMarkerDetail);
    return () => document.removeEventListener('pointerdown', dismissMarkerDetail);
  }, []);
  if (isLoading) {
    return (
      <div className="chart-loading fade-in">
        <Spinner
          animation="border"
          role="status"
          aria-label="Loading chart data"
          className="chart-loading-spinner"
        />
        <h3 style={{ color: '#cccccc', marginTop: '1rem' }}>Loading Chart Data...</h3>
      </div>
    );
  }

  if (!data || data.length === 0) {
    return (
      <div className="no-chart-data fade-in">
        <h3>{emptyTitle}</h3>
        {emptyDescription && <p>{emptyDescription}</p>}
      </div>
    );
  }

  // Decide portfolio line color
  let lineColor = '#4caf50'; // default green
  if (trendDirection === 'down') {
    lineColor = '#f44336';
  }

  const chartData = [...data].sort((left, right) => Number(left?.date) - Number(right?.date));
  const visibleMarkers = projectMarkersOnAccountSeries(markers, chartData, frequency);
  const activeMarkerId = hoveredMarkerId || focusedMarkerId || clickedMarkerId;
  const activeMarker = visibleMarkers.find((marker) => marker.id === activeMarkerId) || null;
  const yDomain = getPortfolioValueDomain(chartData);

  const tooltipFormatter = (date) => moment(date).local().format('MMM D, YYYY h:mm a');

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      const {
        portfolio_value,
        spy_benchmark,
      } = payload[0].payload || {};

      return (
        <Card className="tooltip-card fade-in">
          <Card.Body>
            {typeof portfolio_value === 'number' && (
              <Card.Title className="tooltip-title">
                <span style={{ color: lineColor }} className="chart-tooltip-name">
                  Account Value:
                </span>
                &nbsp;
                <NumericFormat
                  value={portfolio_value}
                  displayType="text"
                  thousandSeparator
                  prefix="$"
                  decimalScale={2}
                  className="chart-tooltip-value"
                />
              </Card.Title>
            )}
            {typeof spy_benchmark === 'number' && (
              <Card.Title className="tooltip-title mt-1">
                <span style={{ color: '#999999' }} className="chart-tooltip-name">
                  Benchmark:
                </span>
                &nbsp;
                <NumericFormat
                  value={spy_benchmark}
                  displayType="text"
                  thousandSeparator
                  prefix="$"
                  decimalScale={2}
                  className="chart-tooltip-value"
                />
              </Card.Title>
            )}
            <Card.Text className="tooltip-text">{tooltipFormatter(label)}</Card.Text>
          </Card.Body>
        </Card>
      );
    }
    return null;
  };

  const positionMarkerDetail = (event) => {
    const container = chartContainerRef.current;
    const target = event?.currentTarget;
    if (!container || !target?.getBoundingClientRect) return;
    const containerRect = container.getBoundingClientRect();
    const targetRect = target.getBoundingClientRect();
    const detailWidth = Math.min(280, Math.max(containerRect.width - 16, 180));
    const desiredLeft = targetRect.left - containerRect.left + (targetRect.width / 2) - (detailWidth / 2);
    const left = Math.max(8, Math.min(desiredLeft, containerRect.width - detailWidth - 8));
    const targetTop = targetRect.top - containerRect.top;
    const top = targetTop > 128 ? targetTop - 112 : targetTop + targetRect.height + 10;
    setMarkerDetailPosition({ left, top, width: detailWidth });
  };

  const activateMarker = (marker, source, event) => {
    positionMarkerDetail(event);
    if (source === 'hover') setHoveredMarkerId(marker.id);
    if (source === 'focus') setFocusedMarkerId(marker.id);
    if (source === 'click') {
      setClickedMarkerId((current) => current === marker.id ? null : marker.id);
    }
  };

  return (
    <div className="chart-container fade-in" ref={chartContainerRef}>
      {cashFlowAvailability?.status && cashFlowAvailability.status !== 'available' && (
        <div className="chart-data-note" role="status">
          Cash-flow adjustment {cashFlowAvailability.status}. {cashFlowAvailability.reason || ''}
        </div>
      )}
      <ResponsiveContainer width="100%" height={chartHeight || 400}>
        <LineChart
          data={chartData}
          margin={minimal ? { top: 12, right: 8, left: 8, bottom: 8 } : { top: 10, right: 10, left: 0, bottom: 20 }}
        >
          {!minimal && <CartesianGrid stroke="#333" vertical={false} horizontal={false} />}
          {!minimal && (
            <XAxis
              dataKey="date"
              tick={<CustomizedAxisTick frequency={frequency} />}
              type="number"
              scale="time"
              domain={['auto', 'auto']}
              stroke="#cccccc"
            />
          )}
          {minimal && <XAxis dataKey="date" type="number" scale="time" domain={['auto', 'auto']} hide />}
          <YAxis domain={yDomain} hide />
          <Tooltip content={<CustomTooltip />} cursor={false} />
          {!minimal && (
            <Legend
              verticalAlign="top"
              height={30}
              wrapperStyle={{ color: '#ffffff', fontSize: '0.85rem' }}
            />
          )}

          <Line
            type="linear"
            dataKey="portfolio_value"
            name="Account Value"
            stroke={lineColor}
            strokeWidth={4}
            dot={false}
            connectNulls
            isAnimationActive={false}
          />
          {!minimal && (
            <Line
              type="linear"
              dataKey="spy_benchmark"
              name="Benchmark"
              stroke="#999999"
              strokeWidth={4}
              dot={false}
              connectNulls
              isAnimationActive={false}
            />
          )}
          {visibleMarkers.map((marker) => (
            <ReferenceDot
              ifOverflow="visible"
              isFront
              key={marker.id}
              shape={(shapeProps) => (
                <ChartEventMarker
                  {...shapeProps}
                  isActive={activeMarker?.id === marker.id}
                  marker={marker}
                  onActivate={(event) => {
                    const eventType = event.type === 'mouseenter'
                      ? 'hover'
                      : event.type === 'focus'
                        ? 'focus'
                        : 'click';
                    activateMarker(marker, eventType, event);
                  }}
                  onBlur={() => setFocusedMarkerId((current) => current === marker.id ? null : current)}
                  onHoverEnd={() => setHoveredMarkerId((current) => current === marker.id ? null : current)}
                />
              )}
              x={marker.chartTimestamp}
              y={marker.chartValue}
            />
          ))}
        </LineChart>
      </ResponsiveContainer>
      {activeMarker && markerDetailPosition && (
        <div
          className="chart-marker-detail"
          data-testid="chart-marker-detail"
          role="status"
          style={markerDetailPosition}
        >
          <strong>{markerTitle(activeMarker)}</strong>
          <span>{markerDetails(activeMarker)}</span>
          <time dateTime={new Date(activeMarker.chartTimestamp).toISOString()}>
            {tooltipFormatter(activeMarker.chartTimestamp)}
          </time>
        </div>
      )}
    </div>
  );
}
